import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Container, Button, Badge, Nav, Offcanvas, Spinner
} from "react-bootstrap";
import {
    FaSignOutAlt, FaBox, FaUsers, FaBars, FaUser, FaChevronLeft,
    FaChevronRight, FaNetworkWired, FaHistory, FaLock, FaChartBar, FaWpforms, FaThList, FaDollarSign
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import ProductsList from "../Components/Dashboard/Products";
import Customers from "../Components/Dashboard/Customers";
import Users from "../Components/Dashboard/Users";
import Reports from "../Components/Dashboard/Reports";
import Collection from "../Components/Dashboard/Collection";
import Access from "../Components/Dashboard/Access";
import LoginActivity from "../Components/Dashboard/LogActivity";
import PassReset from "../Components/Dashboard/PassReset";
import Application from "../Components/Dashboard/Application";
import CollectionFee from "../Components/Dashboard/CollectionFee";
import { getRoleBadge } from "../Components/Shared/GetRoleBadge";

const DashboardPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [userName, setUserName] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [userAccess, setUserAccess] = useState(null);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const hasFetched = useRef(false);
    const isLoggingOut = useRef(false);
    const [singaporeTime, setSingaporeTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setSingaporeTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const formattedTime = singaporeTime.toLocaleTimeString("en-US", {
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true, timeZone: "Asia/Singapore"
    });
    const formattedDate = singaporeTime.toLocaleDateString("en-US", {
        weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "Asia/Singapore"
    });

    useEffect(() => {
        const fetchUser = async () => {
            if (hasFetched.current || isLoggingOut.current) return;
            hasFetched.current = true;

            try {
                const res = await axios.get("https://www.azzenterprises.com/api/admin/getuser", { withCredentials: true });
                if (!res.data || !res.data.user_name) {
                    toast.error("Please login to access the dashboard");
                    setIsAuthenticated(false);
                    return;
                }
                setUserName(res.data.user_name);
                setUserRole(res.data.user_role);
                setUserAccess(res.data.can_access);
                setIsAuthenticated(true);
                localStorage.setItem("user_name", res.data.user_name);
                localStorage.setItem("user_role", res.data.user_role);
                localStorage.setItem("can_access", res.data.can_access);
            } catch (error) {
                console.error("Fetch user error:", error);
                toast.error("Please login to access the dashboard");
                setIsAuthenticated(false);
            }
        };

        const cachedUser = localStorage.getItem("user_name");
        const cachedRole = localStorage.getItem("user_role");
        const cachedAccess = localStorage.getItem("can_access");

        if (cachedUser && cachedRole) {
            setUserName(cachedUser);
            setUserRole(cachedRole);
            setUserAccess(cachedAccess);
            setIsAuthenticated(true);
        } else {
            fetchUser();
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated === false && !isLoggingOut.current) {
            localStorage.removeItem("user_name");
            localStorage.removeItem("user_role");
            localStorage.removeItem("can_access");
            hasFetched.current = false;

            navigate("/login", { replace: true });
        }
    }, [isAuthenticated, navigate]);

    const ROLE_CONFIG = {
        superadmin: [
            { path: "/products-list", label: "Products", icon: <FaBox /> },
            { path: "/user", label: "Users", icon: <FaUser /> },
            { path: "/access", label: "Access", icon: <FaNetworkWired /> },
            { path: "/activity", label: "Activity", icon: <FaHistory /> },
            { path: "/reset-password", label: "Password", icon: <FaLock /> },
            { path: "/collection", label: "Collection", icon: <FaThList /> },
            { path: "/reports", label: "Reports", icon: <FaChartBar /> },
            { path: "/application", label: "Application", icon: <FaWpforms /> },
            { path: "/collection-fee", label: "Coll Fee", icon: <FaDollarSign /> }
        ],
        supervisor: [
            { path: "/application", label: "Application", icon: <FaWpforms /> }
        ],
        viewer: [
            { path: "/products-list", label: "Products", icon: <FaBox /> },
            { path: "/application", label: "Application", icon: <FaWpforms /> }
        ],
        editor: [
            { path: "/products-list", label: "Products", icon: <FaBox /> },
            { path: "/application", label: "Application", icon: <FaWpforms /> }
        ]
    };

    const navItems = [
        { path: "/customers", label: "Customers", icon: <FaUsers /> },
        ...(ROLE_CONFIG[userRole] || []),
    ];

    const handleLogout = async () => {
        if (isLoggingOut.current) return;

        isLoggingOut.current = true;
        try {
            await axios.post("https://www.azzenterprises.com/api/admin/logout", {}, {
                withCredentials: true
            });
        } catch (error) {
            console.error("Logout API error:", error);
        } finally {
            localStorage.removeItem("user_name");
            localStorage.removeItem("user_role");
            localStorage.removeItem("can_access");

            setUserName(null);
            setUserRole(null);
            setUserAccess(null);
            setIsAuthenticated(false);
            hasFetched.current = false;

            toast.success("Logged out successfully");

            setTimeout(() => {
                navigate("/login", { replace: true });
            }, 1000);
        }
    };

    const pageTitles = {
        "/customers": "Customers",
        "/products-list": "Products",
        "/user": "Role-Based Access (RBAC)",
        "/access": "Customer Privileges",
        "/activity": "Staff Activity Log",
        "/reset-password": "Change Password",
        "/collection": "Collection Management - Overdue",
        "/reports": "EMI Payment & Collection Reports",
        "/application": "Application",
        "/collection-fee": "Collection Fee Management",
    };

    const renderContent = () => {
        if (isAuthenticated === null) {
            return (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            );
        }

        if (!isAuthenticated) {
            return null;
        }

        switch (location.pathname) {
            case "/customers": return <Customers />;
            case "/products-list": return <ProductsList />;
            case "/user": return <Users />;
            case "/access": return <Access />;
            case "/activity": return <LoginActivity />;
            case "/reset-password": return <PassReset />;
            case "/collection": return <Collection />;
            case "/reports": return <Reports />;
            case "/application": return <Application />;
            case "/collection-fee": return <CollectionFee />;

            default:
                return (
                    <div className="p-4 text-center">
                        <h4 className="fw-bold mb-3">Welcome to Your Dashboard</h4>
                        <p className="text-muted">Here's what's happening with your store today.</p>
                        <Button variant="outline-primary" onClick={() => navigate("/products-list")}>
                            Explore Products
                        </Button>
                    </div>
                );
        }
    };

    if (isAuthenticated === null) {
        return (
            <>
                <ToastContainer position="top-right" autoClose={1000} />
                <div className="d-flex justify-content-center align-items-center min-vh-100">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </>
        );
    }

    if (!isAuthenticated) {
        return (
            <>
                <ToastContainer position="top-right" autoClose={1000} />
                <div className="d-flex justify-content-center align-items-center min-vh-100">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Redirecting to login...</span>
                    </Spinner>
                </div>
            </>
        );
    }

    return (
        <Container fluid className="bg-light min-vh-100 p-0 d-flex">
            <div className="d-md-none shadow-sm bg-white p-2 sticky-top w-100" style={{ zIndex: 1030 }}>
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <Button variant="light" className="me-2" onClick={() => setShowMobileMenu(true)}>
                            <FaBars />
                        </Button>
                        <span className="fw-semibold">Welcome,</span>
                        <Badge bg="light" text="dark" className="ms-2">{userName}</Badge>
                    </div>
                    <Button onClick={handleLogout} variant="outline-danger" size="sm" className="d-flex align-items-center gap-2">
                        <FaSignOutAlt />
                        <span className="d-none d-md-inline">Logout</span>
                    </Button>
                </div>
            </div>

            <div
                className="d-none d-md-flex flex-column bg-white shadow-sm p-3 transition-all sidebar-sticky"
                style={{
                    width: isSidebarCollapsed ? "60px" : "20%",
                    minWidth: isSidebarCollapsed ? "60px" : "170px",
                    maxWidth: isSidebarCollapsed ? "60px" : "170px",
                    height: "100vh",
                    position: "sticky",
                    top: 0,
                    overflowY: "auto",
                    zIndex: 1020,
                    flexShrink: 0
                }}
            >
                <div className="d-flex justify-content-end mb-3">
                    <Button
                        variant="light"
                        size="sm"
                        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                        className="d-flex align-items-center"
                    >
                        {isSidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
                    </Button>
                </div>

                {!isSidebarCollapsed && (
                    <div className="mb-4">
                        <div className="fw-semibold mb-1 text-muted">Welcome</div>
                        <Badge bg="primary" text="light">{userName}</Badge>
                    </div>
                )}

                <Nav className="flex-column mb-4 flex-grow-1">
                    {navItems.map((item) => (
                        <Nav.Link
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            className={`mb-2 d-flex align-items-center rounded px-2 py-2 small ${location.pathname === item.path
                                ? "text-primary fw-semibold"
                                : "text-secondary"
                                }`}
                            style={{ cursor: "pointer", fontSize: "14px" }}
                        >
                            <span className="me-2">{item.icon}</span>
                            {!isSidebarCollapsed && item.label}
                        </Nav.Link>
                    ))}
                </Nav>

                {!isSidebarCollapsed && (
                    <div className="mt-auto">
                        <Button
                            onClick={handleLogout}
                            variant="outline-danger"
                            className="w-100 d-flex align-items-center justify-content-center gap-1"
                            style={{ cursor: "pointer", fontSize: "14px" }}
                        >
                            <FaSignOutAlt />
                            Logout
                        </Button>
                    </div>
                )}
            </div>

            <div
                className="flex-grow-1 p-3 p-md-4"
                style={{
                    width: isSidebarCollapsed ? "calc(100% - 60px)" : "80%",
                    minHeight: "100vh"
                }}
            >
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                    <h4 className="fw-bold mb-3 mb-md-0">{pageTitles[location.pathname] || "Dashboard"}</h4>
                    <div className="d-flex flex-column align-items-end">
                        <div className="text-muted small bg-white p-2 rounded mb-1 d-flex align-items-center gap-2">
                            <span>{formattedDate} {formattedTime}</span>
                            {getRoleBadge(userRole)}
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-3 shadow-sm p-3 p-md-0">{renderContent()}</div>
            </div>

            <Offcanvas show={showMobileMenu} onHide={() => setShowMobileMenu(false)} placement="start" className="w-75">
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title className="fw-bold">Dashboard</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="p-0">
                    <Nav className="flex-column">
                        {navItems.map((item) => (
                            <Nav.Link
                                key={item.path}
                                onClick={() => {
                                    navigate(item.path);
                                    setShowMobileMenu(false);
                                }}
                                className={`px-4 py-3 d-flex align-items-center border-bottom ${location.pathname === item.path ? "bg-primary text-white" : "text-dark"
                                    }`}
                            >
                                <span className="me-3">{item.icon}</span>
                                {item.label}
                            </Nav.Link>
                        ))}
                    </Nav>
                </Offcanvas.Body>
            </Offcanvas>
        </Container>
    );
};

export default DashboardPage;